import { Component } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CloudLightning, LayoutDashboard, List, BarChart2, AlertTriangle } from "lucide-react";
import StormsDashboard from "@/pages/storms/StormsDashboard";
import SeasonsPage from "@/pages/storms/SeasonsPage";
import SeasonDetailPage from "@/pages/storms/SeasonDetailPage";
import StormDetailPage from "@/pages/storms/StormDetailPage";
import SummaryStatsPage from "@/pages/storms/SummaryStatsPage";

const TABS = [
  { id: "dashboard", label: "Current Season", icon: LayoutDashboard },
  { id: "seasons", label: "All Seasons", icon: List },
  { id: "summary", label: "Summary Stats", icon: BarChart2 },
];

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="border border-red-900/40 bg-[#1a0a0a] p-4 flex items-start gap-3 text-red-400">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          <p className="text-sm">Something went wrong: {this.state.error?.message}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function NamedStormsPage() {
  const { tab: tabParam, season: seasonParam, stormName: stormNameParam } = useParams();
  const navigate = useNavigate();

  const validTabs = TABS.map((t) => t.id);

  // Derive active tab from the URL — season/storm detail lives under the "seasons" tab.
  const activeTab = seasonParam
    ? "seasons"
    : validTabs.includes(tabParam) ? tabParam : "dashboard";

  // Derive selected season and storm from URL params.
  const selectedSeason = seasonParam ? decodeURIComponent(seasonParam) : null;
  const selectedStorm =
    seasonParam && stormNameParam
      ? { name: decodeURIComponent(stormNameParam), season: selectedSeason }
      : null;

  function handleSelectSeason(season) {
    navigate(`/storms/seasons/${encodeURIComponent(season)}`);
  }

  function handleBackToSeasons() {
    navigate("/storms/seasons");
  }

  function handleSelectStorm(stormName) {
    if (!selectedSeason) return;
    navigate(
      `/storms/seasons/${encodeURIComponent(selectedSeason)}/${encodeURIComponent(stormName)}`
    );
  }

  function handleBackFromStormToSeason() {
    if (!selectedSeason) {
      navigate("/storms/seasons");
      return;
    }
    navigate(`/storms/seasons/${encodeURIComponent(selectedSeason)}`);
  }

  function handleBackFromStormToSeasons() {
    navigate("/storms/seasons");
  }

  function handleTabChange(tabId) {
    navigate(`/storms/${tabId}`);
  }

  function renderContent() {
    switch (activeTab) {
      case "dashboard":
        return <StormsDashboard />;
      case "seasons":
        if (selectedStorm) {
          return (
            <StormDetailPage
              name={selectedStorm.name}
              season={selectedStorm.season}
              onBack={handleBackFromStormToSeasons}
              onBackToSeason={handleBackFromStormToSeason}
            />
          );
        }
        if (selectedSeason) {
          return (
            <SeasonDetailPage
              season={selectedSeason}
              onBack={handleBackToSeasons}
              onSelectStorm={handleSelectStorm}
            />
          );
        }
        return <SeasonsPage onSelectSeason={handleSelectSeason} />;
      case "summary":
        return <SummaryStatsPage />;
      default:
        return null;
    }
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <section className="border-b border-white/5 py-10">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center gap-3 mb-2">
            <CloudLightning className="w-6 h-6 text-[#FBBF24]" />
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#F9FAFB] uppercase tracking-tight">
              Named Storms
            </h1>
          </div>
          <p className="text-[#9CA3AF] max-w-2xl text-sm">
            UK & Ireland named storm records
          </p>
        </div>
      </section>

      {/* Tabs */}
      <section className="border-b border-white/5 bg-[#111827]/50">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          {/* Mobile: dropdown select */}
          <div className="md:hidden py-3">
            <select
              value={activeTab}
              onChange={(e) => handleTabChange(e.target.value)}
              aria-label="Select named storms section"
              className="w-full text-xs font-medium uppercase tracking-widest bg-[#0d1117] border border-white/10 text-[#F9FAFB] px-4 py-2.5 focus:outline-none focus:border-[#FBBF24]/40 cursor-pointer"
            >
              {TABS.map(({ id, label }) => (
                <option key={id} value={id}>{label}</option>
              ))}
            </select>
          </div>
          {/* Desktop: horizontal underline tab buttons */}
          <div className="hidden md:flex gap-0">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => handleTabChange(id)}
                className={`flex items-center gap-2 px-5 py-4 text-xs uppercase tracking-widest font-medium border-b-2 transition-colors ${
                  activeTab === id
                    ? "border-[#FBBF24] text-[#FBBF24]"
                    : "border-transparent text-[#9CA3AF] hover:text-[#F9FAFB]"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-10">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <ErrorBoundary key={`${activeTab}-${selectedSeason ?? ""}-${selectedStorm?.name ?? ""}`}>
            {renderContent()}
          </ErrorBoundary>
        </div>
      </section>
    </div>
  );
}

